import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useFolderDetail, useFolderReleaseIds, useUpdateFolder, useDeleteFolder, useRemoveReleaseFromFolder } from '../../hooks/useFolders'
import { getFolderReleaseIds } from '../../api/folders'
import { useRecommendations, usePageTabs, useDiscogsMembership } from '../../hooks'
import { useToast } from '../../context/ToastContext'
import { useViewSort } from '../../context/ViewSortContext'
import { useDiscogsConnection } from '../../context/DiscogsContext'
import axiosClient from '../../api/client'
import { LoadingState } from '../../components/common/Spinner'
import { ReleaseGrid } from '../../components/releases/ReleaseGrid'
import { ReleaseListView } from '../../components/releases/ReleaseListView'
import { Button, IconButton, PageTabBar, Pagination, ReleaseToolbar } from '../../components/common'
import { ReleaseFilters } from '../../components/releases/ReleaseFilters'
import type { ReleaseFilters as ReleaseFiltersType } from '../../types/release'
import styles from './FolderPage.module.css'
import pageStyles from '../PageStyles.module.css'
import releaseStyles from '../../components/releases/Releases.module.css'

type FolderTabFilters = ReleaseFiltersType
const DEFAULT_FOLDER_FILTERS: FolderTabFilters = { sort: 'date', order: 'desc', page: 1, per_page: 50 }

export function FolderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { viewMode, sort, order } = useViewSort()
  const { isAuthenticated } = useDiscogsConnection()
  const { isInWantlist, addToWantlist } = useDiscogsMembership()
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [showRecommendations, setShowRecommendations] = useState(() => {
    try { return localStorage.getItem('folder:showRecommendations') === 'true' } catch { return false }
  })
  const [showFilters, setShowFilters] = useState(false)
  const [search, setSearch] = useState('')
  const [recLimit, setRecLimit] = useState(12)
  const [wantlistBulk, setWantlistBulk] = useState<{ adding: boolean; done: number; total: number } | null>(null)
  const bulkAbortRef = useRef(false)

  const folderId = parseInt(id || '0', 10)

  const [folderFilters, setFolderFilters] = useState<FolderTabFilters>({ ...DEFAULT_FOLDER_FILTERS, sort, order })

  const { savedSearches: tabs, activeTabId, handleTabClick, handleCreateTab, handleDeleteTab } = usePageTabs<FolderTabFilters>({
    scope: `folder:${folderId}` as const,
    filters: folderFilters,
    setFilters: setFolderFilters,
    setSearch,
    defaultFilters: DEFAULT_FOLDER_FILTERS,
  })

  useEffect(() => {
    if (activeTabId === null) setFolderFilters(prev => ({ ...prev, sort, order, page: 1 }))
  }, [sort, order, activeTabId])

  const { data, isLoading, isError } = useFolderDetail(folderId, folderFilters)
  const { data: releaseIdsData } = useFolderReleaseIds(folderId, showRecommendations || showFilters)
  const sourceIds = releaseIdsData?.release_ids ?? []
  const { data: recData, isLoading: recLoading } = useRecommendations(sourceIds, { per_page: recLimit }, { enabled: showRecommendations && sourceIds.length > 0 })
  const updateFolder = useUpdateFolder()
  const deleteFolder = useDeleteFolder()
  const removeRelease = useRemoveReleaseFromFolder()

  const folder = data?.folder
  const releases = data?.releases || []
  const pagination = data?.pagination
  const activeFilterCount = (folderFilters.genres?.length ?? 0) + (folderFilters.styles?.length ?? 0) + (folderFilters.countries?.length ?? 0) +
    (folderFilters.year_from ? 1 : 0) + (folderFilters.year_to ? 1 : 0) +
    (folderFilters.min_want ? 1 : 0) + (folderFilters.min_median ? 1 : 0) + (folderFilters.max_median ? 1 : 0)

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setFolderFilters(prev => ({ ...prev, q: value || undefined, page: 1 }))
  }, [])

  const handleAddAllToWantlist = async () => {
    if (!isAuthenticated || wantlistBulk?.adding) return
    const { release_ids } = await getFolderReleaseIds(folderId)
    const toAdd = release_ids.filter(id => !isInWantlist(id))
    if (toAdd.length === 0) { showToast('All releases already in wantlist', 'info'); return }
    if (!window.confirm(`Add ${toAdd.length} release${toAdd.length !== 1 ? 's' : ''} to your Discogs wantlist?`)) return
    bulkAbortRef.current = false
    setWantlistBulk({ adding: true, done: 0, total: toAdd.length })
    let done = 0
    for (const releaseId of toAdd) {
      if (bulkAbortRef.current) break
      try {
        await axiosClient.post('/discogs/wantlist', { releaseId })
        addToWantlist(releaseId)
      } catch { /* skip failed items */ }
      done++
      setWantlistBulk({ adding: true, done, total: toAdd.length })
      if (done < toAdd.length) await new Promise(r => setTimeout(r, 1100))
    }
    setWantlistBulk(null)
    showToast(`Added ${done} release${done !== 1 ? 's' : ''} to wantlist`, 'success')
  }

  const handleDeleteFolder = async () => {
    if (!folder) return
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${folder.name}"? This will remove the folder but won't delete the releases.`
    )
    if (!confirmDelete) return
    try {
      await deleteFolder.mutateAsync(folderId)
      showToast('Folder deleted successfully', 'success')
      navigate('/releases')
    } catch (error) {
      showToast('Failed to delete folder', 'error')
    }
  }

  const handleStartEdit = () => {
    if (!folder) return
    setEditedName(folder.name)
    setIsEditingName(true)
  }

  const handleSaveName = async () => {
    if (!folder || !editedName.trim()) { setIsEditingName(false); return }
    if (editedName.trim() === folder.name) { setIsEditingName(false); return }
    try {
      await updateFolder.mutateAsync({ id: folderId, updates: { name: editedName.trim() } })
      showToast('Folder renamed successfully', 'success')
      setIsEditingName(false)
    } catch (error) {
      showToast('Failed to rename folder', 'error')
    }
  }

  const handleCancelEdit = () => { setIsEditingName(false); setEditedName('') }

  const handleRemoveRelease = async (releaseId: number) => {
    try {
      await removeRelease.mutateAsync({ folderId, releaseId })
      showToast('Release removed from folder', 'success')
    } catch (error) {
      showToast('Failed to remove release', 'error')
    }
  }

  if (isLoading) {
    return <div className={pageStyles.page}><LoadingState message="Loading folder..." /></div>
  }

  if (isError || !folder) {
    return (
      <div className={pageStyles.page}>
        <div className={pageStyles.empty}>
          <i className="bi bi-folder-x"></i>
          <p>Folder not found</p>
          <Button variant="secondary" onClick={() => navigate('/releases')}>Back to Releases</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        {isEditingName ? (
          <div className={styles.nameEditContainer}>
            <input type="text" className={styles.nameInput} value={editedName} onChange={(e) => setEditedName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') handleCancelEdit() }}
              autoFocus maxLength={50} />
            <IconButton variant="primary" icon="bi bi-check-lg" aria-label="Save name" onClick={handleSaveName} disabled={updateFolder.isPending} />
            <IconButton variant="secondary" icon="bi bi-x-lg" aria-label="Cancel" onClick={handleCancelEdit} disabled={updateFolder.isPending} />
          </div>
        ) : (
          <h1>
            {folder.name}
            {folder.release_count != null && <span className={pageStyles.countBadge}>{folder.release_count.toLocaleString()}</span>}
          </h1>
        )}
        {!isEditingName && (
          <div className={pageStyles.headerActions}>
            {isAuthenticated && (
              <Button
                variant="secondary"
                icon="bi bi-heart"
                onClick={handleAddAllToWantlist}
                disabled={!!wantlistBulk?.adding}
                title="Add all releases in this folder to your Discogs wantlist"
              >
                {wantlistBulk?.adding ? `${wantlistBulk.done}/${wantlistBulk.total}` : 'Add all to wantlist'}
              </Button>
            )}
            <Button variant="secondary" icon="bi bi-pencil" onClick={handleStartEdit} title="Rename folder">Rename</Button>
            <Button variant="danger" icon="bi bi-trash" onClick={handleDeleteFolder} disabled={deleteFolder.isPending} title="Delete folder">Delete</Button>
          </div>
        )}
      </div>
      <PageTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        defaultLabel="All Releases"
        onTabClick={handleTabClick}
        onCreateTab={handleCreateTab}
        onDeleteTab={handleDeleteTab}
      />

      <ReleaseToolbar
        search={search}
        onSearchChange={setSearch}
        onSearchSubmit={handleSearch}
        searchPlaceholder={`Search ${folder.name}...`}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(prev => !prev)}
        filters={folderFilters}
        onFiltersChange={update => setFolderFilters(prev => ({ ...prev, ...update, page: 1 }))}
        activeFilterCount={activeFilterCount}
        trailing={
          <label className={styles.toggleLabel}>
            <span
              className={`${styles.toggleSwitch}${showRecommendations ? ' ' + styles.toggleSwitchOn : ''}`}
              onClick={() => setShowRecommendations(v => {
                const next = !v
                localStorage.setItem('folder:showRecommendations', String(next))
                return next
              })}
            >
              <span className={styles.toggleThumb} />
            </span>
            Recommendations
          </label>
        }
        viewSortProps={{
          sort: folderFilters.sort,
          order: folderFilters.order,
          onSortChange: (nextSort) => setFolderFilters(prev => ({ ...prev, sort: nextSort, page: 1 })),
          onToggleOrder: () => setFolderFilters(prev => ({ ...prev, order: prev.order === 'asc' ? 'desc' : 'asc', page: 1 })),
        }}
      />

      {showFilters && (
        <ReleaseFilters
          filters={folderFilters}
          onFiltersChange={setFolderFilters}
          onClose={() => setShowFilters(false)}
          contextReleaseIds={sourceIds}
        />
      )}

      <div className={releaseStyles.releasesLayout}>
        <div className={releaseStyles.tableContainer}>
          <ReleaseListView
            releases={releases}
            viewMode={viewMode}
            draggable={true}
            onRemove={handleRemoveRelease}
            showRemoveButton={true}
            emptyIcon="bi bi-inbox"
            emptyMessage={folderFilters.q || activeFilterCount > 0 ? 'No matching releases' : 'No releases in this folder'}
            emptyHint={folderFilters.q || activeFilterCount > 0 ? 'Try adjusting your filters' : 'Drag and drop releases here from any page'}
          />

          {pagination && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.total_pages}
              hasNext={pagination.page < pagination.total_pages}
              hasPrev={pagination.page > 1}
              onPageChange={(page) => setFolderFilters(prev => ({ ...prev, page }))}
              perPage={folderFilters.per_page}
              onPerPageChange={(per_page) => setFolderFilters(prev => ({ ...prev, per_page, page: 1 }))}
            />
          )}
        </div>
      </div>

      {showRecommendations && (
        <div className={styles.recommendSection}>
          <div className={styles.recommendHeader}>
            <span className={styles.recommendTitle}>Recommended</span>
          </div>
          {recLoading ? (
            <div style={{ color: 'var(--stonks-text-muted)', fontSize: '0.875rem' }}>Loading recommendations…</div>
          ) : recData?.releases && recData.releases.length > 0 ? (
            <>
              <ReleaseGrid releases={recData.releases} draggable={true} />
              {recData.releases.length >= recLimit && (
                <div className={styles.loadMoreRow}>
                  <Button variant="secondary" onClick={() => setRecLimit(prev => prev + 12)} disabled={recLoading}>
                    Load more
                  </Button>
                </div>
              )}
            </>
          ) : sourceIds.length === 0 ? (
            <div style={{ color: 'var(--stonks-text-muted)', fontSize: '0.875rem' }}>Add releases to this folder to get recommendations.</div>
          ) : (
            <div style={{ color: 'var(--stonks-text-muted)', fontSize: '0.875rem' }}>No recommendations found.</div>
          )}
        </div>
      )}
    </div>
  )
}
